"use client"

import { create } from "zustand"
import { persist } from "zustand/middleware"
import { anyTermMatches } from "@/lib/blacklist-match"

export type TagFilterType = "love" | "hate"

export interface TagFilter {
  tag: string
  type: TagFilterType
}

interface TagFiltersStore {
  tagFilters: TagFilter[]
  enableFilters: boolean
  filterMode: "blacklist" | "whitelist" | "hybrid"
  addFilter: (tag: string, type: TagFilterType) => void
  removeFilter: (tag: string) => void
  toggleFilterType: (tag: string) => void
  getFilterType: (tag: string) => TagFilterType | null
  toggleFilters: () => void
  setFilterMode: (mode: "blacklist" | "whitelist" | "hybrid") => void
  isPostFiltered: (tags: string[]) => boolean
}

export const useTagFiltersStore = create<TagFiltersStore>()(
  persist(
    (set, get) => ({
      tagFilters: [],
      enableFilters: true,
      filterMode: "blacklist",

      addFilter: (tag: string, type: TagFilterType) => {
        const cleanTag = tag.trim().toLowerCase()
        if (!cleanTag) return

        set((state) => {
          const existing = state.tagFilters.find((f) => f.tag === cleanTag)
          if (existing) return state

          return {
            tagFilters: [...state.tagFilters, { tag: cleanTag, type }],
          }
        })
      },

      removeFilter: (tag: string) => {
        const cleanTag = tag.trim().toLowerCase()
        set((state) => ({
          tagFilters: state.tagFilters.filter((f) => f.tag !== cleanTag),
        }))
      },

      toggleFilterType: (tag: string) => {
        const cleanTag = tag.trim().toLowerCase()
        set((state) => ({
          tagFilters: state.tagFilters.map((f) =>
            f.tag === cleanTag ? { ...f, type: f.type === "love" ? "hate" : "love" } : f,
          ),
        }))
      },

      getFilterType: (tag: string) => {
        const cleanTag = tag.trim().toLowerCase()
        const filter = get().tagFilters.find((f) => f.tag === cleanTag)
        return filter?.type || null
      },

      toggleFilters: () => {
        set((state) => ({ enableFilters: !state.enableFilters }))
      },

      setFilterMode: (mode: "blacklist" | "whitelist" | "hybrid") => {
        set({ filterMode: mode })
      },

      /**
       * Loved tags only. "Hated" tags moved to the blacklist (Settings > Blacklist), so they are no longer acted on here (any old
       * entries are kept but ignored). "whitelist" shows only posts with a loved tag; "blacklist" (the default) just keeps the list.
       */
      isPostFiltered: (tags: string[]) => {
        const { tagFilters, enableFilters, filterMode } = get()
        if (!enableFilters || filterMode !== "whitelist") return false
        const loveTags = tagFilters.filter((f) => f.type === "love").map((f) => f.tag)
        if (loveTags.length === 0) return false
        return !anyTermMatches(loveTags, tags.map((t) => t.toLowerCase()))
      },
    }),
    {
      name: "tag-filters",
      version: 1,
      // "hybrid" meant loved + hated; with hated tags moved to the blacklist it is the same as "whitelist".
      migrate: (persisted: any) => (persisted && persisted.filterMode === "hybrid" ? { ...persisted, filterMode: "whitelist" } : persisted),
    },
  ),
)
